
import { addTeacherNotification, getNotificationById } from './db-operations';
import { TeacherNotification } from './types';
import { Teacher } from '@/types/teacher.types';
import { format, addDays, isBefore } from 'date-fns';

// Generate payment notification for a teacher
export const generatePaymentNotification = async (teacher: Teacher): Promise<string | null> => {
  try {
    if (!teacher || !teacher.id) return null;
    
    // Safety check for salary
    if (!teacher.salary) {
      console.warn('Teacher has no salary information:', teacher.id);
      return null;
    }
    
    const today = new Date();
    let dueDate: Date;
    let paymentLabel: string;
    
    // Check if teacher has payment date set in salary info
    if (teacher.salaryInfo?.paymentDate) {
      // Use the last payment date to calculate the next due date (monthly)
      const lastPaymentDate = new Date(teacher.salaryInfo.paymentDate);
      dueDate = new Date(lastPaymentDate.getFullYear(), lastPaymentDate.getMonth() + 1, lastPaymentDate.getDate());
      paymentLabel = 'Next';
    } else {
      // Default to the 1st of next month
      dueDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      paymentLabel = 'Upcoming';
    }
    
    // Only create notification if payment is upcoming within next 7 days
    const sevenDaysFromNow = addDays(today, 7);
    if (!isBefore(dueDate, sevenDaysFromNow)) {
      return null;
    }
    
    // Format the amount properly
    const amount = typeof teacher.salary === 'string' 
      ? parseFloat(teacher.salary || '0') 
      : (teacher.salary || 0);
    
    const formattedAmount = amount.toString();
    const daysToPayment = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    // Set priority based on days remaining
    let priority = 'informational';
    if (daysToPayment <= 0) priority = 'urgent';
    else if (daysToPayment <= 3) priority = 'important';
    
    const notification: TeacherNotification = {
      id: crypto.randomUUID(),
      teacherId: teacher.id,
      type: 'payment',
      contentType: 'salary-reminder',
      title: `${paymentLabel} Salary Payment Due`,
      message: `${teacher.firstName} ${teacher.lastName}'s salary payment of ${formattedAmount} AFN is due ${daysToPayment <= 0 ? 'today' : `in ${daysToPayment} days`} (${format(dueDate, 'PP')}).`,
      createdAt: new Date(),
      isRead: false,
      read: false,
      date: today.toISOString(),
      metadata: {
        amount: amount,
        dueDate: dueDate,
        actionUrl: `/teachers/${teacher.id}/payment`,
        hasAction: true,
        actionText: "Process Payment",
        priority: priority,
        daysToPayment: daysToPayment
      }
    };
    
    return addTeacherNotification(notification);
  } catch (error) {
    console.error('Error generating payment notification:', error);
    return null;
  }
};

// Check for upcoming salary payments
export const checkSalaryReminders = async (): Promise<TeacherNotification[]> => {
  try {
    const db = await import('./db-operations').then(mod => mod.getDB());
    const teachers = await db.getAll('teachers');
    const notifications: TeacherNotification[] = [];
    
    for (const teacher of teachers) {
      if (teacher.salary) {
        // Check if there's already a recent notification for this teacher
        const existingNotifications = await db.getAllFromIndex(
          'notifications',
          'byTeacherAndContent',
          [teacher.id, 'salary-reminder']
        );
        
        // Only create a new notification if there are no recent ones (in the last 24 hours)
        const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
        const hasRecentNotification = existingNotifications.some(n => 
          new Date(n.createdAt) > dayAgo
        );
        
        if (!hasRecentNotification) {
          // Create a payment notification
          const notificationId = await generatePaymentNotification(teacher);
          
          if (notificationId) {
            const notification = await getNotificationById(notificationId);
            if (notification) notifications.push(notification);
          }
        }
      }
    }
    
    return notifications;
  } catch (error) {
    console.error('Error checking for salary reminders:', error);
    return [];
  }
};

// Track teacher payment history and setup next payment
export const trackTeacherPayment = async (
  teacherId: string, 
  paymentAmount: number, 
  paymentPeriod: string
): Promise<void> => {
  try {
    const db = await import('./db-operations').then(mod => mod.getDB());
    const teacher = await db.get('teachers', teacherId);
    
    if (!teacher) return;
    
    // Update teacher payment status
    if (teacher.paymentStatus !== 'Paid') {
      teacher.paymentStatus = 'Paid';
      
      // Update salary info
      teacher.salaryInfo = {
        ...teacher.salaryInfo,
        paymentDate: new Date().toISOString(),
        amount: paymentAmount,
        frequency: teacher.salaryInfo?.frequency || 'Monthly'
      };
      
      await db.put('teachers', teacher);
      
      // Clear any existing payment notifications
      const existingNotifications = await db.getAllFromIndex(
        'notifications',
        'byTeacherAndContent',
        [teacherId, 'salary-reminder']
      );
      
      for (const notification of existingNotifications) {
        if (!notification.isRead) {
          notification.isRead = true;
          notification.read = true;
          await db.put('notifications', notification);
        }
      }
      
      // Generate next payment notification (usually for next month)
      setTimeout(() => {
        generatePaymentNotification(teacher);
      }, 1000); // Delay slightly to ensure DB operations are complete
    }
  } catch (error) {
    console.error('Error tracking teacher payment:', error);
  }
};
